import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getModulesForCourse } from '../services/moduleService';
import { getQuestions } from '../services/questionService';
import {
  selectModule1Questions,
  selectModule2Questions,
  getModule2Tier,
  toScaledScore,
} from '../utils/satAdaptive';
import '../styles/satExam.css';

const RW_SUBJECT = 'SAT Reading & Writing';
const MATH_SUBJECT = 'SAT Math';
const BREAK_SECONDS = 600;

const EXAM_MODULES = [
  { key: 'rw1',   section: 'rw',   label: 'Reading & Writing', number: 1, count: 27, minutes: 32 },
  { key: 'rw2',   section: 'rw',   label: 'Reading & Writing', number: 2, count: 27, minutes: 32 },
  { key: 'math1', section: 'math', label: 'Math',              number: 1, count: 22, minutes: 35 },
  { key: 'math2', section: 'math', label: 'Math',              number: 2, count: 22, minutes: 35 },
];

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function isCorrect(question, answers) {
  const selected = answers[String(question._id)];
  return question.options?.some((o) => o.id === selected && o.correct) ?? false;
}

// ─── Loading ────────────────────────────────────────────────────────────────
function LoadingScreen() {
  return (
    <div className="sat-shell sat-shell--centered">
      <p className="sat-loading-text">Preparing your exam…</p>
    </div>
  );
}

// ─── Error ──────────────────────────────────────────────────────────────────
function ErrorScreen({ message, onBack }) {
  return (
    <div className="sat-shell sat-shell--centered">
      <p className="sat-error-text">{message}</p>
      <button className="sat-btn sat-btn--primary" onClick={onBack}>Back to Practice</button>
    </div>
  );
}

// ─── Intro ──────────────────────────────────────────────────────────────────
function IntroScreen({ courseLabel, rwPool, mathPool, onBegin }) {
  const rwM1 = Math.min(EXAM_MODULES[0].count, rwPool.length);
  const mathM1 = Math.min(EXAM_MODULES[2].count, mathPool.length);
  const poolWarning = rwPool.length < EXAM_MODULES[0].count || mathPool.length < EXAM_MODULES[2].count;

  return (
    <div className="sat-shell sat-shell--centered">
      <div className="sat-intro-card">
        <p className="sat-eyebrow">SAT Practice Exam</p>
        <h1 className="sat-intro-title">{courseLabel}</h1>

        <div className="sat-intro-sections">
          <div className="sat-intro-row">
            <span className="sat-intro-section-name">Reading &amp; Writing</span>
            <span className="sat-intro-section-detail">2 modules · {rwM1}Q each · 32 min each</span>
          </div>
          <div className="sat-intro-divider" />
          <div className="sat-intro-row">
            <span className="sat-intro-section-name">Math</span>
            <span className="sat-intro-section-detail">2 modules · {mathM1}Q each · 35 min each</span>
          </div>
        </div>

        <p className="sat-intro-meta">
          Stage-adaptive · ~2 hours 14 minutes · 10-minute break between sections
        </p>
        <p className="sat-intro-sub">
          Module 1 sets the difficulty for Module 2. Answers are locked once a module is submitted.
        </p>

        {poolWarning && (
          <p className="sat-pool-warning">
            Note: fewer questions are available than the standard SAT count. The exam will use all available questions.
          </p>
        )}

        <button className="sat-btn sat-btn--primary sat-btn--lg" onClick={onBegin}>
          Begin Exam
        </button>
      </div>
    </div>
  );
}

// ─── Module ──────────────────────────────────────────────────────────────────
function ModuleScreen({
  mod, questions, currentQuestionIndex, answers, timeLeft,
  onAnswer, onNavigate, onSubmit,
}) {
  const question = questions[currentQuestionIndex];
  if (!question) return null;

  const qId = String(question._id);
  const selectedAnswer = answers[qId];
  const answeredCount = questions.filter((q) => answers[String(q._id)] !== undefined).length;
  const isUrgent = timeLeft < 300;

  return (
    <div className="sat-shell sat-shell--exam">
      {/* Header */}
      <header className="sat-header">
        <div className="sat-header-left">
          <span className="sat-badge sat-badge--section">{mod.label}</span>
          <span className="sat-badge sat-badge--module">Module {mod.number}</span>
        </div>

        <div className="sat-header-center">
          <div className="sat-nav-grid">
            {questions.map((q, i) => {
              const answered = answers[String(q._id)] !== undefined;
              const current = i === currentQuestionIndex;
              return (
                <button
                  key={String(q._id)}
                  className={`sat-nav-dot${answered ? ' answered' : ''}${current ? ' current' : ''}`}
                  onClick={() => onNavigate(i)}
                  aria-label={`Question ${i + 1}${answered ? ', answered' : ''}`}
                  aria-current={current ? 'true' : undefined}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>
        </div>

        <div className="sat-header-right">
          <span className={`sat-timer${isUrgent ? ' sat-timer--urgent' : ''}`}>
            {formatTime(timeLeft)}
          </span>
        </div>
      </header>

      {/* Question */}
      <main className="sat-main">
        <div className="sat-question-card">
          <p className="sat-question-counter">
            Question {currentQuestionIndex + 1} <span className="sat-question-of">of {questions.length}</span>
          </p>

          {question.paragraph && (
            <div className="sat-paragraph-box">
              <p className="sat-paragraph">{question.paragraph}</p>
            </div>
          )}

          <p className="sat-stem">{question.stem}</p>

          <div className="sat-options">
            {question.options?.map((option) => (
              <button
                key={option.id}
                className={`sat-option${selectedAnswer === option.id ? ' sat-option--selected' : ''}`}
                onClick={() => onAnswer(qId, option.id)}
              >
                <span className="sat-option-bubble">{option.id}</span>
                <span className="sat-option-text">{option.text}</span>
              </button>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="sat-footer">
        <span className="sat-answered-count">
          {answeredCount} of {questions.length} answered
        </span>
        <div className="sat-footer-nav">
          <button
            className="sat-btn sat-btn--secondary"
            onClick={() => onNavigate(Math.max(0, currentQuestionIndex - 1))}
            disabled={currentQuestionIndex === 0}
          >
            Back
          </button>
          <button
            className="sat-btn sat-btn--secondary"
            onClick={() => onNavigate(Math.min(questions.length - 1, currentQuestionIndex + 1))}
            disabled={currentQuestionIndex === questions.length - 1}
          >
            Next
          </button>
          <button className="sat-btn sat-btn--primary" onClick={onSubmit}>
            Submit Module
          </button>
        </div>
      </footer>
    </div>
  );
}

// ─── Transition ──────────────────────────────────────────────────────────────
function TransitionScreen({ completedModIndex, onContinue }) {
  const completed = EXAM_MODULES[completedModIndex];
  const next = EXAM_MODULES[completedModIndex + 1];
  return (
    <div className="sat-shell sat-shell--centered">
      <div className="sat-transition-card">
        <p className="sat-eyebrow">{completed.label} · Module {completed.number}</p>
        <h2 className="sat-transition-title">Module {completed.number} Submitted</h2>
        <p className="sat-transition-body">
          Your answers are locked. Next: Module {next.number} — {next.count} questions, {next.minutes} minutes.
        </p>
        <button className="sat-btn sat-btn--primary sat-btn--lg" onClick={onContinue}>
          Begin Module {next.number}
        </button>
      </div>
    </div>
  );
}

// ─── Break ──────────────────────────────────────────────────────────────────
function BreakScreen({ breakTimeLeft, onContinue }) {
  return (
    <div className="sat-shell sat-shell--centered">
      <div className="sat-break-card">
        <p className="sat-eyebrow">Section Break</p>
        <h2 className="sat-break-title">Reading &amp; Writing Complete</h2>
        <p className="sat-break-body">Take a short break. Math begins next.</p>
        <div className="sat-break-timer">{formatTime(breakTimeLeft)}</div>
        <p className="sat-break-note">The Math section starts automatically when the timer ends.</p>
        <button className="sat-btn sat-btn--primary" onClick={onContinue}>
          Begin Math Now
        </button>
      </div>
    </div>
  );
}

// ─── Results ─────────────────────────────────────────────────────────────────
function ResultsScreen({ courseLabel, moduleQuestions, answers, onBack }) {
  const [reviewSection, setReviewSection] = useState('rw');

  const allRwQs = [...moduleQuestions[0], ...moduleQuestions[1]];
  const allMathQs = [...moduleQuestions[2], ...moduleQuestions[3]];
  const rwCorrect = allRwQs.filter((q) => isCorrect(q, answers)).length;
  const mathCorrect = allMathQs.filter((q) => isCorrect(q, answers)).length;
  const rwScaled = toScaledScore(rwCorrect, allRwQs.length);
  const mathScaled = toScaledScore(mathCorrect, allMathQs.length);
  const composite = rwScaled + mathScaled;

  const reviewQs = reviewSection === 'rw' ? allRwQs : allMathQs;

  return (
    <div className="sat-shell sat-shell--results">
      <header className="sat-results-header">
        <div>
          <p className="sat-eyebrow">SAT Practice Results</p>
          <h1 className="sat-results-title">{courseLabel}</h1>
        </div>
        <button className="sat-btn sat-btn--secondary" onClick={onBack}>
          Back to Practice
        </button>
      </header>

      {/* Score cards */}
      <div className="sat-score-row">
        {[
          { label: 'Reading & Writing', correct: rwCorrect, total: allRwQs.length, scaled: rwScaled },
          { label: 'Math',              correct: mathCorrect, total: allMathQs.length, scaled: mathScaled },
        ].map((s) => (
          <div key={s.label} className="sat-score-card">
            <p className="sat-eyebrow">{s.label}</p>
            <p className="sat-score-scaled">{s.scaled}</p>
            <p className="sat-score-raw">{s.correct} / {s.total} correct</p>
          </div>
        ))}
        <div className="sat-score-card sat-score-card--composite">
          <p className="sat-eyebrow">Composite</p>
          <p className="sat-score-scaled">{composite}</p>
          <p className="sat-score-raw">out of 1600</p>
        </div>
      </div>

      {/* Question review */}
      <div className="sat-review">
        <div className="sat-review-tabs">
          {[
            { key: 'rw', label: `Reading & Writing (${allRwQs.length}Q)` },
            { key: 'math', label: `Math (${allMathQs.length}Q)` },
          ].map((tab) => (
            <button
              key={tab.key}
              className={`sat-review-tab${reviewSection === tab.key ? ' active' : ''}`}
              onClick={() => setReviewSection(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="sat-review-list">
          {reviewQs.map((q, i) => {
            const qId = String(q._id);
            const userAnswer = answers[qId];
            const correct = isCorrect(q, answers);
            const correctOption = q.options?.find((o) => o.correct);

            return (
              <div key={qId} className={`sat-review-q${correct ? ' sat-review-q--correct' : ' sat-review-q--incorrect'}`}>
                <div className="sat-review-q-header">
                  <span className="sat-review-q-num">Q{i + 1}</span>
                  <span className={`sat-review-verdict${correct ? ' correct' : ' incorrect'}`}>
                    {correct ? 'Correct' : 'Incorrect'}
                  </span>
                  {!userAnswer && <span className="sat-review-skipped">Not answered</span>}
                </div>

                {q.paragraph && (
                  <p className="sat-review-paragraph">{q.paragraph}</p>
                )}
                <p className="sat-review-stem">{q.stem}</p>

                <div className="sat-review-options">
                  {q.options?.map((opt) => {
                    const isUserChoice = opt.id === userAnswer;
                    const isCorrectOpt = opt.correct;
                    return (
                      <div
                        key={opt.id}
                        className={`sat-review-option${isCorrectOpt ? ' is-correct' : ''}${isUserChoice && !isCorrectOpt ? ' is-wrong' : ''}`}
                      >
                        <span className="sat-option-bubble">{opt.id}</span>
                        <span className="sat-option-text">{opt.text}</span>
                        <div className="sat-review-tags">
                          {isUserChoice && <span className="sat-review-tag sat-review-tag--user">Your answer</span>}
                          {isCorrectOpt && <span className="sat-review-tag sat-review-tag--correct">Correct</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {correctOption?.rationale && (
                  <p className="sat-review-rationale">{correctOption.rationale}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function SatSession() {
  const location = useLocation();
  const navigate = useNavigate();
  const { courseId, courseLabel } = location.state || {};

  const [phase, setPhase] = useState('loading');
  const [loadError, setLoadError] = useState('');
  const [rwPool, setRwPool] = useState([]);
  const [mathPool, setMathPool] = useState([]);
  const [moduleQuestions, setModuleQuestions] = useState([[], [], [], []]);
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [breakTimeLeft, setBreakTimeLeft] = useState(BREAK_SECONDS);

  // Stable ref so timer expiry always calls the latest handleModuleComplete
  const completeRef = useRef(null);

  // ── Data loading ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!courseId) {
      setLoadError('No course specified. Return to Practice and try again.');
      setPhase('error');
      return;
    }
    let cancelled = false;
    Promise.all([getModulesForCourse(courseId), getQuestions({ courseId })])
      .then(([modulesData, questionsData]) => {
        if (cancelled) return;
        const modules = modulesData?.modules || [];
        const questions = questionsData?.questions || [];

        const rwIds = new Set(
          modules.filter((m) => m.subject === RW_SUBJECT).map((m) => String(m._id || m.id)),
        );
        const mathIds = new Set(
          modules.filter((m) => m.subject === MATH_SUBJECT).map((m) => String(m._id || m.id)),
        );

        const rwQs = questions.filter((q) => rwIds.has(String(q.moduleId)));
        const mathQs = questions.filter((q) => mathIds.has(String(q.moduleId)));

        const rw1Qs = selectModule1Questions(rwQs, EXAM_MODULES[0].count);
        const math1Qs = selectModule1Questions(mathQs, EXAM_MODULES[2].count);

        setRwPool(rwQs);
        setMathPool(mathQs);
        setModuleQuestions([rw1Qs, [], math1Qs, []]);
        setPhase('intro');
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err.message || 'Unable to load exam. Please try again.');
        setPhase('error');
      });
    return () => { cancelled = true; };
  }, [courseId]);

  // ── Module completion ────────────────────────────────────────────────────────
  const handleModuleComplete = useCallback(() => {
    const idx = currentModuleIndex;
    if (idx === 0) {
      const tier = getModule2Tier(moduleQuestions[0], answers);
      const used = moduleQuestions[0].map((q) => String(q._id));
      const rw2 = selectModule2Questions(rwPool, EXAM_MODULES[1].count, tier, used);
      setModuleQuestions((prev) => { const n = [...prev]; n[1] = rw2; return n; });
      setPhase('transition');
    } else if (idx === 1) {
      setBreakTimeLeft(BREAK_SECONDS);
      setPhase('break');
    } else if (idx === 2) {
      const tier = getModule2Tier(moduleQuestions[2], answers);
      const used = moduleQuestions[2].map((q) => String(q._id));
      const math2 = selectModule2Questions(mathPool, EXAM_MODULES[3].count, tier, used);
      setModuleQuestions((prev) => { const n = [...prev]; n[3] = math2; return n; });
      setPhase('transition');
    } else {
      setPhase('results');
    }
  }, [currentModuleIndex, moduleQuestions, answers, rwPool, mathPool]);

  useEffect(() => { completeRef.current = handleModuleComplete; }, [handleModuleComplete]);

  // ── Countdown ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'module' || timeLeft <= 0) return;
    const id = setTimeout(() => setTimeLeft((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [phase, timeLeft]);

  // Timer expiry — calls via ref so it always has current state
  useEffect(() => {
    if (phase === 'module' && timeLeft === 0) {
      completeRef.current?.();
    }
  }, [phase, timeLeft]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Break countdown ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'break') return;
    if (breakTimeLeft <= 0) { startModule(2); return; }
    const id = setTimeout(() => setBreakTimeLeft((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [phase, breakTimeLeft]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const startModule = useCallback((index) => {
    setCurrentModuleIndex(index);
    setCurrentQuestionIndex(0);
    setTimeLeft(EXAM_MODULES[index].minutes * 60);
    setPhase('module');
  }, []);

  const handleAnswer = useCallback((qId, optionId) => {
    setAnswers((prev) => ({ ...prev, [qId]: optionId }));
  }, []);

  // ── Render ───────────────────────────────────────────────────────────────────
  if (phase === 'loading') return <LoadingScreen />;
  if (phase === 'error') return <ErrorScreen message={loadError} onBack={() => navigate('/practice')} />;

  if (phase === 'intro') {
    return (
      <IntroScreen
        courseLabel={courseLabel}
        rwPool={rwPool}
        mathPool={mathPool}
        onBegin={() => startModule(0)}
      />
    );
  }

  if (phase === 'module') {
    return (
      <ModuleScreen
        mod={EXAM_MODULES[currentModuleIndex]}
        questions={moduleQuestions[currentModuleIndex]}
        currentQuestionIndex={currentQuestionIndex}
        answers={answers}
        timeLeft={timeLeft}
        onAnswer={handleAnswer}
        onNavigate={setCurrentQuestionIndex}
        onSubmit={handleModuleComplete}
      />
    );
  }

  if (phase === 'transition') {
    return (
      <TransitionScreen
        completedModIndex={currentModuleIndex}
        onContinue={() => startModule(currentModuleIndex + 1)}
      />
    );
  }

  if (phase === 'break') {
    return (
      <BreakScreen
        breakTimeLeft={breakTimeLeft}
        onContinue={() => startModule(2)}
      />
    );
  }

  return (
    <ResultsScreen
      courseLabel={courseLabel}
      moduleQuestions={moduleQuestions}
      answers={answers}
      onBack={() => navigate('/practice')}
    />
  );
}
